<script setup>
import VClamp from "vue-clamp";
import {inject, ref, watch} from "vue";
import needsStartupsService from "@/services/needs-startups.service";
import {formatNumber} from "../../../helpers/string";
import {getLogo} from "@/helpers/logo";
import useMarkdown from "@/composables/useMarkdown";

const props = defineProps(['startup']);

const needId = inject('needId');
const showDetails = ref(false);
const startupDetails = ref(null);
const md = useMarkdown();

watch(showDetails, (value) => {
    if (value) {
        getStartupInfo();
    }
});

watch(() => props.startup, () => {
    showDetails.value = false;
});

function formateDateForNews(string) {
    const date = new Date(string);

    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
}

async function getStartupInfo() {
    startupDetails.value = null;

    const {data} = await needsStartupsService.getStartupDetails(needId, props.startup.id);
    startupDetails.value = data;
}

defineExpose({
    showDetails
})
</script>

<template>
    <div>
        <header class="modal-matchmaking__content__header">
            <div class="image-block">
                <img :src="getLogo(startup.logo)" alt=""/>
            </div>
            <div>
                <h5 class="heading5 heading5--secondary">{{ startup.name }}</h5>
                <!-- <p class="modal-matchmaking__content__by modal-matchmaking__content__by--recommended">Recommended by AI</p> -->
                <p v-if="false"
                   class="modal-matchmaking__content__by modal-matchmaking__content__by--sent">Sent by
                    NEA</p>
            </div>

            <div class="vc-label">AI Recommended</div>

            <div v-if="showDetails" @click="showDetails = false"
                 class="modal-matchmaking__minimize"></div>
        </header>

        <template v-if="!showDetails">
            <div class="modal-matchmaking__content__inner">
                <div class="modal-matchmaking__stat">
                    <div class="modal-matchmaking__stat__item modal-matchmaking__stat__item--match">
                        njknkkjbkbk
                    </div>

                    <div class="modal-matchmaking__stat__item modal-matchmaking__stat__item--score">
                        IRL Score: {{ startup.irl_score }}/10
                    </div>
                </div>
                <!--                                 <p>Vel pharetra vel turpis nunc eget lorem dolor sed viverra.</p>-->
                <!--                                 <p>Nec sagittis aliquam malesuada bibendum arcu vitae elementum curabitur vitae.</p>-->
                <p class="modal-matchmaking__content__text">
                    <VClamp autoresize :max-lines="5"> {{ startup.description }}
                        <template #after="{ clamped }">
                            <a v-if="clamped" class="link-normal" @click="showDetails = true"> more</a>
                        </template>
                    </VClamp>
                </p>

                <template v-if="startup.clients.length">
                    <h5 class="heading5 heading5--secondary">Clients:</h5>
                    <ul class="list">
                        <li v-for="client in startup.clients" :key="client">
                            {{ client }}
                        </li>
                    </ul>
                </template>

                <div v-if="false" class="modal-matchmaking__textarea">
                    <p>Malcolm Reynolds, <b>NEA</b>:</p>
                    <p><i>Gamma Core is an exciting startup that has been scaling up quickly and would
                        be a good fit for your need. They spent the last year working with Lorem Corp
                        successfully and are ready for their next challenge.</i></p>
                </div>
            </div>
            <footer class="modal-matchmaking__content__footer">
                <a href="#" @click.prevent="showDetails = true">More Details</a>
            </footer>
        </template>

        <template v-else-if="startupDetails">
            <div class="modal-matchmaking__content__inner">
                <div class="modal-matchmaking__content__details">
                    <ul class="matchmaking__overview">
                        <li class="matchmaking__overview__name">
                            <a href="#">{{startupDetails.website }}</a>
                        </li>
                        <li v-if="startupDetails.total_raised" class="matchmaking__overview__funding">
                            {{ formatNumber(startupDetails.total_raised) }} Raised
                        </li>
                        <li v-if="startupDetails.revenue_range" class="matchmaking__overview__income">
                            {{ startupDetails.revenue_range }} Annual Income
                        </li>
                        <li v-if="startupDetails.employees" class="matchmaking__overview__employees">
                            {{ startupDetails.employees }} Employees
                        </li>
                        <li v-if="startupDetails.location" class="matchmaking__overview__location">
                            {{ startupDetails.location }}
                        </li>
                        <li v-if="startupDetails.stage" class="matchmaking__overview__startups">
                            {{ startupDetails.stage }}
                        </li>
                        <li v-if="startupDetails.founded" class="matchmaking__overview__founded">Founded
                            {{ startupDetails.founded }}
                        </li>
                    </ul>

                    <div class="matchmaking__about">
                        <h5 class="heading5 heading5--secondary mb-4">About {{startup.name}}:</h5>

                        <p>{{ startup.description }}</p>

                        <div class="modal-matchmaking__stat">
                            <div
                                class="modal-matchmaking__stat__item modal-matchmaking__stat__item--match">
                                njknkkjbkbk
                            </div>

                            <div
                                class="modal-matchmaking__stat__item modal-matchmaking__stat__item--score">
                                IRL Score: {{ startup.irl_score }}/10
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="startupDetails.features?.length" class="card-headline">
                    <header class="card-headline__header">Features</header>

                    <div class="card-headline__content">
                        <div v-html="md.render(startupDetails.features)"/>
                    </div>
                </div>

                <div v-if="startupDetails.news.length" class="card-headline">
                    <header class="card-headline__header">News</header>

                    <div class="card-headline__content">
                        <div
                            v-for="news in startupDetails.news"
                            :key="news.id"
                            class="card card--bordered"
                        >
                            <div>
                                <a :href="`https://${news.domain}`" class="card-headline__url" target="_blank">{{ news.domain }}</a>

                                <h6 class="heading6">
                                    <a :href="news.url" target="_blank">{{ news.title }}</a>
                                </h6>

                                <time v-if="news.posted_on" class="card-headline__time">
                                    {{formateDateForNews(news.posted_on) }}
                                </time>
                            </div>

                            <figure v-if="news.image_url" class="card-headline__img">
                                <img :src="news.image_url" style="height: 100%" alt="">
                            </figure>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<style scoped>

</style>
